/**
 * ElementBuilder constants — tags, defaults, icon helpers.
 * URL safety lives in ContentComposer (shared XSS kernel).
 */
import {
  safeUrl as ccSafeUrl,
  SAFE_URL_PATTERN
} from '../ContentComposer/ContentComposer.js';

export { SAFE_URL_PATTERN };

export const VOID_TAGS = Object.freeze(new Set([
  'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input',
  'link', 'meta', 'param', 'source', 'track', 'wbr'
]));

/** Tags that must never be emitted. */
export const FORBIDDEN_TAGS = Object.freeze(new Set([
  'script', 'iframe', 'object', 'embed', 'link', 'meta', 'style', 'base'
]));

/**
 * UI tags allowed by default. Unknown non-forbidden tags are still allowed
 * unless in FORBIDDEN_TAGS (open allow with deny-list for dangerous tags).
 */
export const ALLOWED_TAGS = Object.freeze(new Set([
  'a', 'abbr', 'address', 'article', 'aside', 'b', 'bdi', 'bdo', 'blockquote',
  'br', 'button', 'caption', 'cite', 'code', 'col', 'colgroup', 'data', 'datalist',
  'dd', 'del', 'details', 'dfn', 'dialog', 'div', 'dl', 'dt', 'em', 'fieldset',
  'figcaption', 'figure', 'footer', 'form', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'header', 'hgroup', 'hr', 'i', 'img', 'input', 'ins', 'kbd', 'label', 'legend',
  'li', 'main', 'mark', 'menu', 'meter', 'nav', 'ol', 'optgroup', 'option',
  'output', 'p', 'picture', 'pre', 'progress', 'q', 'rp', 'rt', 'ruby', 's',
  'samp', 'section', 'select', 'small', 'span', 'strong', 'sub', 'summary',
  'sup', 'table', 'tbody', 'td', 'textarea', 'tfoot', 'th', 'thead', 'time',
  'tr', 'u', 'ul', 'var', 'wbr'
]));

export const DEFAULT_BUTTON_CLASS = 'btn-outline-secondary';

export const URL_ATTRS = Object.freeze(new Set(['href', 'src']));

/**
 * Normalize Bootstrap Icon class strings.
 * @param {string|null|undefined} icon
 * @returns {string}
 */
export function normalizeIcon(icon) {
  if (!icon) return '';
  const raw = String(icon).trim();
  if (!raw) return '';
  if (raw.startsWith('bi ')) return raw;
  if (raw.startsWith('bi-')) return `bi ${raw}`;
  return `bi bi-${raw}`;
}

/**
 * Shared XSS kernel (ContentComposer). Prefix keeps EB warn logs identifiable.
 * @param {string} url
 * @returns {string}
 */
export function safeUrl(url) {
  return ccSafeUrl(url, 'ElementBuilder');
}

/**
 * @param {string} tag
 * @returns {boolean}
 */
export function isForbiddenTag(tag) {
  return FORBIDDEN_TAGS.has(String(tag || '').toLowerCase());
}

/**
 * @param {string} tag
 * @returns {boolean}
 */
export function isVoidTag(tag) {
  return VOID_TAGS.has(String(tag || '').toLowerCase());
}
